import React, { useState, useEffect } from 'react';

const terminalLines = [
  { text: "Hi 👋 Abhay here.", delay: 1500 },
  { text: "", delay: 300 },
  { text: "Engineering scalable systems.", delay: 1200 },
  { text: "Crafting intelligent cloud products.", delay: 1200 },
  { text: "Shaping the next-gen tech ecosystem.", delay: 1500 },
  { text: "", delay: 500 },
  { text: "_ Craft. Code. Scale. _", delay: 2000 }
];

const TerminalIntro = ({ onComplete }) => {
  const [currentLine, setCurrentLine] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (currentLine >= terminalLines.length) {
      setTimeout(() => {
        setIsCompleting(true);
        setTimeout(() => {
          onComplete();
        }, 1500); // Wait for blur animation to complete
      }, 2000);
      return;
    }

    const line = terminalLines[currentLine];
    let textIndex = 0;

    const typeWriter = setInterval(() => {
      if (textIndex <= line.text.length) {
        setCurrentText(line.text.slice(0, textIndex));
        textIndex++;
      } else {
        clearInterval(typeWriter);
        setTimeout(() => {
          setCurrentLine(prev => prev + 1);
          setCurrentText('');
        }, line.delay);
      }
    }, 60);

    return () => clearInterval(typeWriter);
  }, [currentLine, onComplete]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #0f0f23 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      fontFamily: 'Monaco, "Lucida Console", monospace',
      padding: windowWidth <= 768 ? '0.5rem' : '1rem',
      boxSizing: 'border-box',
      opacity: isCompleting ? 0 : 1,
      filter: isCompleting ? 'blur(20px)' : 'blur(0px)',
      transition: 'opacity 1.5s ease-out, filter 1.5s ease-out'
    }}>
      <div style={{
        background: 'rgba(0, 0, 0, 0.8)',
        border: '2px solid #00ff88',
        borderRadius: '12px',
        padding: windowWidth <= 768 ? '0.75rem' : '2rem',
        width: windowWidth <= 768 ? '100%' : 'auto',
        maxWidth: windowWidth <= 768 ? 'calc(100vw - 1rem)' : '500px',
        minHeight: windowWidth <= 768 ? '250px' : '300px',
        boxShadow: '0 0 30px rgba(0, 255, 136, 0.3)',
        position: 'relative',
        margin: '0 auto',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {/* Terminal Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: windowWidth <= 768 ? '1rem' : '1.5rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #333'
        }}>
          <div style={{
            width: windowWidth <= 768 ? '10px' : '12px',
            height: windowWidth <= 768 ? '10px' : '12px',
            borderRadius: '50%',
            background: '#ff5f56',
            marginRight: windowWidth <= 768 ? '6px' : '8px'
          }}></div>
          <div style={{
            width: windowWidth <= 768 ? '10px' : '12px',
            height: windowWidth <= 768 ? '10px' : '12px',
            borderRadius: '50%',
            background: '#ffbd2e',
            marginRight: windowWidth <= 768 ? '6px' : '8px'
          }}></div>
          <div style={{
            width: windowWidth <= 768 ? '10px' : '12px',
            height: windowWidth <= 768 ? '10px' : '12px',
            borderRadius: '50%',
            background: '#27ca3f',
            marginRight: windowWidth <= 768 ? '12px' : '16px'
          }}></div>
          <span style={{
            color: '#00ff88',
            fontSize: windowWidth <= 768 ? '11px' : '14px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}>
            {windowWidth <= 768 ? 'abhay@portfolio:~$' : 'abhay@portfolio:~$'}
          </span>
        </div>

        {/* Terminal Content */}
        <div style={{ 
          minHeight: windowWidth <= 768 ? '150px' : '200px',
          overflow: 'hidden'
        }}>
          {terminalLines.slice(0, currentLine).map((line, index) => (
            <div key={index} style={{
              color: '#00ff88',
              marginBottom: '8px',
              fontSize: windowWidth <= 768 ? '14px' : '16px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {line.text.trim() && <span style={{ color: '#888', marginRight: '8px' }}>{'>'}</span>}
              <span style={{ 
                wordBreak: 'break-word',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                maxWidth: '100%',
                overflow: 'hidden'
              }}>{line.text}</span>
            </div>
          ))}
          
          {currentLine < terminalLines.length && (
            <div style={{
              color: '#00ff88',
              marginBottom: '8px',
              fontSize: windowWidth <= 768 ? '14px' : '16px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {(currentText.trim() || terminalLines[currentLine].text.trim()) && <span style={{ color: '#888', marginRight: '8px' }}>{'>'}</span>}
              <span style={{ 
                wordBreak: 'break-word',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                maxWidth: '100%',
                overflow: 'hidden'
              }}>{currentText}</span>
              <span style={{
                color: '#00ff88',
                opacity: showCursor ? 1 : 0,
                marginLeft: '2px',
                animation: 'blink 1s infinite'
              }}>
                |
              </span>
            </div>
          )}
        </div>

        {/* Loading Animation */}
        <div style={{
          position: 'absolute',
          bottom: '1rem',
          right: '1rem',
          display: 'flex',
          gap: '4px'
        }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#00ff88',
                animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite`
              }}
            />
          ))}
        </div>
      </div>

      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}
      </style>
    </div>
  );
};

export default TerminalIntro;